import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useDiscoverStore } from '../stores/discover-store'
import { TAG_GROUPS } from '../types/tags'

export function TagsSelector() {
  const { tags } = useDiscoverStore((s) => s.searchParams)
  const { handleSetTags, handleSetTagsSelectorOpen } = useDiscoverStore(
    (s) => s.handlers
  )

  // 局部状态管理选择
  const [localTags, setLocalTags] = useState<string[]>(tags)

  // 当外部 tags 改变时同步到局部
  useEffect(() => {
    setLocalTags(tags)
  }, [tags])

  // 处理多选标签（次标签）
  const handleToggleTag = (tag: string) => {
    setLocalTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  // 处理互斥标签（主标签和次标签中的互斥组如 position）
  const handleSelectExclusiveTag = (
    groupKey: 'identity' | 'orientation' | 'position',
    value: string
  ) => {
    const group = TAG_GROUPS[groupKey]
    const otherTags = localTags.filter(
      (t) => !(group.tags as string[]).includes(t)
    )

    if (value === 'all') {
      setLocalTags(otherTags)
    } else {
      setLocalTags([...otherTags, value])
    }
  }

  const getSelectedValue = (groupKey: 'identity' | 'orientation' | 'position') => {
    const group = TAG_GROUPS[groupKey]
    return localTags.find((t) => (group.tags as string[]).includes(t)) || 'all'
  }

  const handleConfirm = () => {
    handleSetTags(localTags)
    handleSetTagsSelectorOpen(false)
  }

  const handleReset = () => {
    setLocalTags([])
  }

  // 定义需要渲染的次标签组 Key
  const subGroupKeys = ['genre', 'style', 'character'] as const

  return (
    <div className='flex max-h-[80vh] flex-col gap-6 overflow-y-auto'>
      {/* 1. 主标签：身份与类型 */}
      <section className='space-y-4'>
        <h3 className='text-base font-semibold'>主标签</h3>
        <div className='space-y-4 px-1'>
          {(['identity', 'orientation'] as const).map((key) => (
            <div key={key} className='flex items-start gap-4'>
              <span className='text-muted-foreground w-16 shrink-0 pt-1 text-sm'>
                {TAG_GROUPS[key].label}
              </span>
              <div className='flex flex-wrap gap-2'>
                <Badge
                  variant={
                    getSelectedValue(key) === 'all' ? 'default' : 'secondary'
                  }
                  className='cursor-pointer rounded-full'
                  onClick={() => handleSelectExclusiveTag(key, 'all')}
                >
                  全部
                </Badge>
                {TAG_GROUPS[key].tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={
                      getSelectedValue(key) === tag ? 'default' : 'secondary'
                    }
                    className='cursor-pointer rounded-full'
                    onClick={() => handleSelectExclusiveTag(key, tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* 2. 次标签：分层选择 */}
      <section className='space-y-6'>
        <h3 className='text-base font-semibold'>次标签</h3>
        <div className='space-y-6 px-1'>
          {/* 左右位标签（互斥） */}
          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <span className='text-muted-foreground px-2 py-0.5 text-lg font-medium'>
                {TAG_GROUPS.position.label}
              </span>
            </div>
            <div className='flex flex-wrap gap-2'>
              <Badge
                variant={getSelectedValue('position') === 'all' ? 'default' : 'secondary'}
                className={cn(
                  'h-8 cursor-pointer rounded-full border px-4 transition-all active:scale-95',
                  getSelectedValue('position') === 'all'
                    ? 'bg-primary text-primary-foreground border-transparent'
                    : 'hover:bg-muted'
                )}
                onClick={() => handleSelectExclusiveTag('position', 'all')}
              >
                全部
              </Badge>
              {TAG_GROUPS.position.tags.map((tag) => {
                const isSelected = getSelectedValue('position') === tag
                return (
                  <Badge
                    key={tag}
                    variant={isSelected ? 'default' : 'secondary'}
                    className={cn(
                      'h-8 cursor-pointer rounded-full border px-4 transition-all active:scale-95',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-transparent'
                        : 'hover:bg-muted'
                    )}
                    onClick={() => handleSelectExclusiveTag('position', tag)}
                  >
                    {tag}
                  </Badge>
                )
              })}
            </div>
          </div>

          {/* 其他次标签（多选） */}
          {subGroupKeys.map((groupKey) => {
            const group = TAG_GROUPS[groupKey]
            return (
              <div key={groupKey} className='space-y-3'>
                <div className='flex items-center gap-2'>
                  <span className='text-muted-foreground px-2 py-0.5 text-lg font-medium'>
                    {group.label}
                  </span>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {group.tags.map((tag) => {
                    const isSelected = localTags.includes(tag)
                    return (
                      <Badge
                        key={tag}
                        variant={isSelected ? 'default' : 'secondary'}
                        className={cn(
                          'h-8 cursor-pointer rounded-full border px-4 transition-all active:scale-95',
                          isSelected
                            ? 'bg-primary text-primary-foreground border-transparent'
                            : 'hover:bg-muted'
                        )}
                        onClick={() => handleToggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* 3. 底部操作固定 */}
      <div className='bg-background sticky -bottom-2 flex items-center justify-between pt-6 pb-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={handleReset}
          className='rounded-full px-8'
        >
          重置
        </Button>
        <div className='text-muted-foreground flex items-center gap-2 text-xs'>
          已选 {localTags.length} 个
          <Button
            size='sm'
            onClick={handleConfirm}
            className='ml-2 rounded-full px-8'
          >
            确认
          </Button>
        </div>
      </div>
    </div>
  )
}
